(() => {
  if (customElements.get('kalo-collections')) return;

  const listen = (target, type, callback, controller) => target.addEventListener(type, callback, { signal: controller.signal });
  const collectionsChanged = () => document.dispatchEvent(new CustomEvent('kalo:collections-changed'));

  class KaloCollections extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.bundles = [...this.querySelectorAll('[data-bundle]')];
      this.tablist = this.querySelector('[data-bundle-tabs]');
      this.tabs = [];
      this.galleries = [];
      if (!this.bundles.length) return;

      this.tablist.setAttribute('role', 'tablist');
      this.tablist.setAttribute('aria-label', this.dataset.tabLabel);
      this.tablist.setAttribute('aria-orientation', 'vertical');
      this.bundles.forEach((bundle, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'kalo-tab';
        tab.id = `${bundle.id}-tab`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', bundle.id);
        tab.append(bundle.querySelector('[data-bundle-tab]').content.cloneNode(true));
        listen(tab, 'click', () => this.select(index), this.controller);
        this.tablist.append(tab);
        this.tabs.push(tab);
        bundle.setAttribute('role', 'tabpanel');
        bundle.setAttribute('aria-labelledby', tab.id);
        bundle.tabIndex = 0;
        this.prepareGallery(bundle);
        bundle.querySelector('[data-bundle-children]').setAttribute('role', 'list');
        bundle.querySelectorAll('[data-collection-item]').forEach(item => item.setAttribute('role', 'listitem'));
      });
      this.tablist.hidden = false;
      this.classList.add('is-enhanced');
      this.select(0);

      listen(this.tablist, 'keydown', event => {
        const current = this.tabs.indexOf(event.target);
        if (current < 0) return;
        let next;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
        else return;
        event.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      }, this.controller);

      listen(this, 'click', event => {
        const link = event.target.closest('[data-bundle-enquire]');
        if (!link) return;
        const bundle = link.closest('[data-bundle]');
        const destination = new URL(link.href, location.href);
        if (destination.origin !== location.origin || destination.pathname !== location.pathname || !destination.hash) return;
        const anchor = document.getElementById(decodeURIComponent(destination.hash.slice(1)));
        const enquiry = anchor?.parentElement.querySelector('kalo-enquiry');
        if (enquiry) enquiry.selectCollection(bundle.dataset.title);
      }, this.controller);

      listen(document, 'shopify:block:select', event => {
        const id = event.detail?.blockId;
        const selected = event.target.closest?.('[data-block-id]');
        const block = selected && this.contains(selected) ? selected : [...this.querySelectorAll('[data-block-id]')].find(node => node.dataset.blockId === id);
        const bundle = block?.closest('[data-bundle]');
        if (!bundle) return;
        this.select(this.bundles.indexOf(bundle));
        const gallery = this.galleries.find(entry => entry.bundle === bundle);
        const slide = block.closest('[data-gallery-slide]');
        if (slide && gallery) this.selectImage(gallery, gallery.slides.indexOf(slide));
      }, this.controller);
      this.media = matchMedia('(max-width: 989px)');
      const orientation = () => this.tablist.setAttribute('aria-orientation', this.media.matches ? 'horizontal' : 'vertical');
      listen(this.media, 'change', orientation, this.controller);
      orientation();
      collectionsChanged();
    }

    select(index) {
      this.bundles.forEach((bundle, i) => { bundle.hidden = i !== index; });
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
    }

    prepareGallery(bundle) {
      const stage = bundle.querySelector('[data-gallery-stage]');
      const thumbnails = bundle.querySelector('[data-gallery-thumbnails]');
      const slides = [...bundle.querySelectorAll('[data-gallery-slide]')];
      const gallery = { bundle, stage, thumbnails, slides, buttons: [] };
      slides.forEach((slide, index) => {
        const button = slide.querySelector('[data-gallery-select]');
        stage.append(slide);
        thumbnails.append(button);
        gallery.buttons.push(button);
        button.hidden = false;
        listen(button, 'click', () => this.selectImage(gallery, index), this.controller);
      });
      thumbnails.hidden = slides.length < 2;
      this.selectImage(gallery, 0);
      this.galleries.push(gallery);
    }

    selectImage(gallery, index) {
      gallery.slides.forEach((slide, i) => { slide.hidden = i !== index; });
      gallery.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.galleries?.forEach(gallery => {
        const children = gallery.bundle.querySelector('[data-bundle-children]');
        gallery.slides.forEach((slide, i) => {
          slide.hidden = false;
          gallery.buttons[i].hidden = true;
          slide.append(gallery.buttons[i]);
          children.append(slide);
        });
      });
      this.bundles?.forEach(bundle => { bundle.hidden = false; });
      this.tablist?.replaceChildren();
      queueMicrotask(collectionsChanged);
    }
  }

  class KaloEnquiry extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.input = this.querySelector('[data-collection-input]');
      this.options = this.querySelector('[data-collection-options]');
      this.refresh();
      listen(document, 'kalo:collections-changed', () => this.refresh(), this.controller);
      listen(this.options, 'change', event => { this.input.value = event.target.value; }, this.controller);
      const result = this.querySelector('[data-form-result]');
      if (result) result.focus();
    }

    refresh() {
      const bundles = [...document.querySelectorAll('kalo-collections [data-bundle]')];
      const previous = this.input.value;
      this.options.replaceChildren();
      this.querySelector('[data-collection-fallback]').hidden = bundles.length > 0;
      if (!bundles.length) return;
      const choices = bundles.map(bundle => ({ title: bundle.dataset.title, price: bundle.dataset.price }));
      choices.push({ title: this.dataset.unsure, price: '' });
      choices.forEach((choice, index) => {
        const label = document.createElement('label');
        label.className = 'kalo-choice';
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'kalo-collection-choice';
        radio.value = choice.title;
        radio.id = `KaloChoice-${this.dataset.sectionId}-${index}`;
        radio.checked = previous === choice.title;
        const title = document.createElement('span');
        title.textContent = choice.title;
        label.append(radio, title);
        if (choice.price) {
          const price = document.createElement('small');
          price.textContent = choice.price;
          label.append(price);
        }
        this.options.append(label);
      });
    }

    selectCollection(title) {
      const radio = [...this.options.querySelectorAll('input')].find(input => input.value === title);
      if (!radio) return;
      radio.checked = true;
      this.input.value = title;
    }

    disconnectedCallback() { this.controller?.abort(); }
  }

  class KaloCampaignPopup extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.storageKey = `kalo-campaign-${this.dataset.sectionId}`;
      listen(this, 'click', event => {
        if (event.target.closest('[data-popup-close]')) this.dialog.close();
        if (event.target === this.dialog) {
          const bounds = this.dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
        }
      }, this.controller);
      listen(this.dialog, 'close', () => this.restore(), this.controller);
      listen(this.dialog, 'keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.dialog.close();
        }
      }, this.controller);
      listen(document, 'shopify:section:select', event => {
        if (event.detail?.sectionId === this.dataset.sectionId && this.dataset.editor === 'true') this.open(true);
      }, this.controller);
      listen(document, 'shopify:section:deselect', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.dialog.close();
      }, this.controller);
      if (this.dataset.enabled !== 'true' || this.dataset.editor === 'true') return;
      try {
        if (this.dataset.frequency === 'session' && sessionStorage.getItem(this.storageKey)) return;
      } catch (error) {
        console.warn('KALO campaign popup session storage is unavailable; showing once on this page.', error);
      }
      this.timer = setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
    }

    open(preview = false) {
      if (!this.isConnected || this.dialog.open) return;
      const modalVisible = [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(modal => modal.getClientRects().length && getComputedStyle(modal).visibility !== 'hidden');
      if (!preview && (modalVisible || document.documentElement.classList.contains('drawer-open') || document.body.style.overflow === 'hidden')) return;
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.body.style.overflow;
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
      if (!preview && this.dataset.frequency === 'session') {
        try { sessionStorage.setItem(this.storageKey, '1'); }
        catch (error) { console.warn('KALO campaign popup could not save its session frequency.', error); }
      }
    }

    restore() {
      if (this.previousOverflow !== undefined) document.body.style.overflow = this.previousOverflow;
      if (this.previousFocus?.isConnected) this.previousFocus.focus();
      this.previousOverflow = undefined;
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.dialog?.open) this.dialog.close();
      this.restore();
      this.controller?.abort();
    }
  }

  customElements.define('kalo-collections', KaloCollections);
  customElements.define('kalo-enquiry', KaloEnquiry);
  customElements.define('kalo-campaign-popup', KaloCampaignPopup);
})();
